import { LogisticsRequestQuote } from "../../../types/PartnerCompanyDetails";
import { calculatePriceRange, parseServerDate, timeAgo } from "../../../utils";
import { getRFQStatusStyles } from "../../Admin/CustomerRFQsDetails/RFQCard";
import { AirPlaneIcon, ClockIcon, MapPinIcon, ShipIcon } from "../../Icons";

export type LogisticsRequestQuoteUI = Omit<
  LogisticsRequestQuote,
  "id" | "uid"
> & {
  quotes_id: number | string;
};

export default function QuotesList({
  quotes,
}: {
  quotes: LogisticsRequestQuoteUI[];
}) {
  if (!quotes.length)
    return <p className="text-center text-sm text-gray-400">No RFQs found</p>;

  return (
    <div className="space-y-3">
      {quotes.map((rfq) => {
        const from = rfq.from_json?.name || "—";
        const to = rfq.to_json?.name || "—";

        const freightType = rfq.freight_type || "—";
        const weight = rfq.weight ? `${rfq.weight} kg` : "—";
        const date = rfq.pickup_date || "—";

        const price = rfq.ai_response?.quote?.estimated_price_usd ?? null;

        const status = rfq.status || "Pending";

        const FreightIcon = freightType === "air" ? AirPlaneIcon : ShipIcon;

        const { label, className } = getRFQStatusStyles(status);

        return (
          <div
            key={rfq.quotes_id}
            className="p-4 bg-white border border-gray-100 rounded-xl transition-all duration-200 hover:border-gray-300"
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2 text-gray-700">
                <MapPinIcon className="size-4 text-gray-500" />
                <h3 className="text-sm font-semibold">
                  {from} → {to}
                </h3>
                {rfq.created_at && (
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <ClockIcon className="size-4 text-gray-300" />
                    <span>{timeAgo(parseServerDate(rfq.created_at))}</span>
                  </div>
                )}
              </div>

              <span
                className={`text-xs px-2 py-1 rounded-full font-medium ${
                  className
                }`}
              >
                {label}
              </span>
            </div>

            {/* Sub Info */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <FreightIcon className="size-4 text-blue-500" />
                <span>{freightType.toUpperCase()}</span>
                <span className="text-gray-400">|</span>
                <span>{weight}</span>
                <span className="text-gray-400">|</span>
                <span>{rfq.container_type}</span>
              </div>

              <span className="text-[11px] text-gray-400">Pickup: {date}</span>
            </div>

            {/* Footer */}
            <div className="mt-3 flex justify-between items-center">
              {price ? (
                <div className="flex items-baseline gap-1">
                  <p className="text-sm font-semibold text-green-600">
                    {calculatePriceRange(price).formatted("$")}
                  </p>
                  <span className="text-[11px] text-gray-400">(Estimated)</span>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">Awaiting Quote</p>
              )}

              <span className="text-[11px] text-gray-400">
                RFQ #{rfq.transaction_id}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
